import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";
import { PageHeader } from "@/components/page-header";
import { UsersFilters } from "@/components/users/users-filters";
import { UsersTable } from "@/components/users/users-table";
import { useUsersSearch } from "@/hooks/use-users-search";
import { useRoles } from "@/hooks/use-roles";
import { useExport } from "@/hooks/use-export";
import { usersExportColumns } from "@/lib/export/columns";
import type { ApiSearchUserItem } from "@/types/api-helpers";

export const Route = createFileRoute("/_dashboard/users/")({ component: UsersPage });

function UsersContent() {
  const t = useTranslations("UsersPage");
  const tUserStatus = useTranslations("UserStatus");
  const tRoleLabels = useTranslations("RoleLabels");
  const tUsersTable = useTranslations("UsersTable");
  const locale = useLocale();
  const {
    data,
    isLoading,
    search,
    setSearch,
    status,
    setStatus,
    role,
    setRole,
    sorting,
    onSortingChange,
    paginationState,
    onPaginationChange,
    pageCount,
  } = useUsersSearch();
  const { roles } = useRoles();

  const exportFilters = useMemo(() => ({
    ...(search ? { q: search } : {}),
    ...(status !== "all" ? { status } : {}),
    ...(role !== "all" ? { role } : {}),
  }), [search, status, role]);

  const { exportData, progress: exportProgress, cancel: cancelExport } = useExport<ApiSearchUserItem>({
    endpoint: "/admin/search/users",
    columns: usersExportColumns,
    filters: exportFilters,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headerTranslator: (key) => tUsersTable(key as any),
    translators: {
      locale,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tStatus: (key) => tUserStatus(key as any),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tRole: (key) => tRoleLabels(key as any),
    },
    filenamePrefix: t("exportFilename"),
    pdfTitle: t("title"),
  });

  return (
    <>
      <PageHeader title={t("title")} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <UsersFilters
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          role={role}
          onRoleChange={setRole}
          roles={roles}
          searchPlaceholder={t("searchPlaceholder")}
          onExport={exportData}
          exportProgress={exportProgress}
          onExportCancel={cancelExport}
        />
        <UsersTable
          data={data}
          sorting={sorting}
          onSortingChange={onSortingChange}
          pagination={paginationState}
          onPaginationChange={onPaginationChange}
          pageCount={pageCount}
          isLoading={isLoading}
        />
      </div>
    </>
  );
}

function UsersPage() {
  return (
    <Suspense>
      <UsersContent />
    </Suspense>
  );
}
